import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { DetailGrid, LoadState, PrimaryButton, SecondaryButton } from "./hrComponents";
import { formatDateTime, requestLabel, statusBadge, statusLabel, useLoad } from "./hrUi";

const ACTION_LABELS = { SUBMIT: "Submitted", APPROVE: "Approved", REJECT: "Rejected", SEND_BACK: "Sent back", WITHDRAW: "Withdrawn", ESCALATE: "Escalated", REMIND: "Reminder sent", SKIP: "Skipped" };

// One request: what it is, who has to decide, what has happened so far - and the decision
// buttons when it is the caller's turn. A reason is required to reject or send back.
export default function ApprovalDrawer({ requestId, onClose, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getApproval(requestId), [requestId], { enabled: !!requestId });
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    if (action !== "APPROVE" && !comment.trim()) {
      toast.error(action === "REJECT" ? "Please give a reason for rejecting" : "Please say what needs to change");
      return;
    }
    setBusy(true);
    try {
      const res = await hrmsService.actOnApproval(requestId, action, comment.trim() || undefined);
      if (res?.success) {
        toast.success(res.message || "Done");
        setComment("");
        onChanged && onChanged();
        onClose();
      } else {
        toast.error(hrmsService.responseMessage(res));
      }
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const res = await hrmsService.withdrawApproval(requestId);
      if (res?.success) {
        toast.success(res.message || "Withdrawn");
        onChanged && onChanged();
        onClose();
      } else {
        toast.error(hrmsService.responseMessage(res));
      }
    } finally {
      setBusy(false);
    }
  };

  const details = data?.details ? Object.entries(data.details) : [];
  const canWithdraw = !!data && data.status === "PENDING" && data.is_requester;

  const footer =
    data && data.can_act ? (
      <>
        <SecondaryButton onClick={() => decide("SEND_BACK")} disabled={busy}>
          Send back
        </SecondaryButton>
        <SecondaryButton onClick={() => decide("REJECT")} disabled={busy} style={{ color: "#B91C1C", borderColor: "#B91C1C" }}>
          Reject
        </SecondaryButton>
        <PrimaryButton onClick={() => decide("APPROVE")} disabled={busy}>
          Approve
        </PrimaryButton>
      </>
    ) : canWithdraw ? (
      <>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        <SecondaryButton onClick={withdraw} disabled={busy}>
          Withdraw request
        </SecondaryButton>
      </>
    ) : (
      <SecondaryButton onClick={onClose}>Close</SecondaryButton>
    );

  return (
    <SlidePanel show={!!requestId} onHide={onClose} title={data ? requestLabel(data.module) : "Request"} footer={footer} width={520}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div style={{ fontWeight: 600 }}>{data.requester_name || "—"}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{data.employee_code}</div>
              </div>
              <span style={statusBadge(data.status)}>{statusLabel(data.status)}</span>
            </div>
            {data.summary ? <p style={{ fontSize: 14 }}>{data.summary}</p> : null}
            {details.length ? <DetailGrid items={details.map(([k, v]) => [k, String(v)])} /> : null}

            <h6 className="mt-4 mb-2">Approval steps</h6>
            {(data.approvers || []).length === 0 ? <div style={{ fontSize: 13, color: "#6B7280" }}>No approvers</div> : null}
            {(data.approvers || []).map((a) => (
              <div key={a.id} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <span>
                  Level {a.level_no}: {a.approver_name || "—"}
                  {a.escalated ? <span style={{ color: "#92400E" }}> (escalated)</span> : null}
                </span>
                <span style={statusBadge(a.status)}>{statusLabel(a.status)}</span>
              </div>
            ))}

            <h6 className="mt-4 mb-2">Timeline</h6>
            {(data.timeline || []).map((t) => (
              <div key={t.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid #F3F4F6" }}>
                <div>
                  <strong>{ACTION_LABELS[t.action] || statusLabel(t.action)}</strong> · {t.actor_name || "System"}
                </div>
                <div style={{ color: "#6B7280" }}>{formatDateTime(t.created_at)}</div>
                {t.comment ? <div style={{ marginTop: 2 }}>“{t.comment}”</div> : null}
              </div>
            ))}

            {data.can_act ? (
              <div className="mt-4">
                <label style={{ fontSize: 13, fontWeight: 600 }}>Comment</label>
                <textarea className="form-control" rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Required to reject or send back" />
              </div>
            ) : null}
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}
